import { isOriginal, ORIGINAL_KEYWORD, SITES } from "@mfa/v5-contracts";
import type { CSSProperties } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link } from "react-router-dom";
import type { GalleryTemplate, SiteId } from "../../../core/types";
import { templateColor } from "../category-style";
import styles from "./TemplateCard.module.css";

/**
 * V1 template card (letter avatar, badges, chips) extracted from the MFA Template Library
 * grid. A team carries a "Team · N members" badge and a members line (first three names,
 * +N); a single that sits in teams carries "Also in team: X" or "In N teams"; an Original
 * (a template with the `original` keyword) carries a small marker by its name, beside its
 * topic's chip; a template whose home is the other site says which site; keyword and
 * capability chips show labels, never raw keys, at most three of every chip with "+N" for
 * the rest; the description stops at three lines (workflows/V5-Template-Organization.md
 * Rev 2 §R2.2 "Cards and detail pages"; workflows/V5-Template-Method-Review.md V1, V4, V8).
 * No library name appears.
 *
 * The title is a heading whose link is the card's one interactive element: the link
 * stretches over the card, so a click anywhere opens the template while assistive tech
 * reads the title alone. Category colours live in ../category-style, by the category's
 * place in the shared vocabulary.
 */

/** How many member names the team line spells out before "+N". */
export const MEMBER_NAMES_SHOWN = 3;
/** How many chips a card shows before "+N". */
export const CHIPS_SHOWN = 3;

const ink = "var(--brand-ink)";
const card = "var(--brand-card)";

// Re-exported so existing importers (TemplateDetailView) keep their path.
export { templateColor };

/** The avatar's letter: the first letter or digit of the title, accents folded, upper case. */
export function templateLetter(name: string): string {
  const folded = name.normalize("NFD").replace(/\p{M}+/gu, "");
  return folded.match(/[\p{L}\p{N}]/u)?.[0]?.toLocaleUpperCase() ?? "?";
}

const chipSty: CSSProperties = {
  padding: "2px 10px",
  background: "var(--brand-x-f4efe2)",
  border: "var(--brand-bw-2) solid var(--brand-line-2)",
  borderRadius: "var(--brand-r-999)",
  fontWeight: 700,
  fontSize: "11px",
  color: "var(--brand-lavender-mid)",
};

const teamChipSty: CSSProperties = {
  ...chipSty,
  background: "var(--brand-violet-bg)",
  border: "var(--brand-bw-2) solid var(--brand-violet-line)",
  color: "var(--brand-violet-deep)",
};

const badgeSty: CSSProperties = {
  padding: "3px 11px",
  border: `var(--brand-bw-2) solid ${ink}`,
  borderRadius: "var(--brand-r-999)",
  fontFamily: "var(--brand-font-display)",
  fontWeight: 800,
  fontSize: "10.5px",
  color: ink,
  whiteSpace: "nowrap",
};

/** "Team · N members". */
export function TeamBadge({ count }: { count: number }) {
  return (
    <div data-testid="team-badge" style={{ ...badgeSty, background: "var(--brand-violet-bg)" }}>
      <FormattedMessage id="tpl.card.team" values={{ count }} />
    </div>
  );
}

/** "From Launch Your Agent": a template whose home is not the site showing it. */
export function HomeSiteBadge({ siteId }: { siteId: SiteId }) {
  return (
    <div data-testid="home-site-badge" style={{ ...badgeSty, background: "var(--brand-x-f4efe2)" }}>
      <FormattedMessage id="tpl.card.homeSite" values={{ site: SITES[siteId].name }} />
    </div>
  );
}

type CardChip = { key: string; label: string; team?: boolean; title?: string };

export function TemplateCard({
  entry,
  to,
  state,
  siteId,
  categoryLabels,
  keywordLabels,
  categoryOrder,
}: {
  entry: GalleryTemplate;
  /** The detail the card opens, and the router state it carries (the gallery as background). */
  to: string;
  state?: unknown;
  /** The site showing the gallery: a template from the other one is badged. */
  siteId: string;
  categoryLabels: ReadonlyMap<string, string>;
  /** Keyword and capability key → label; a key without one shows as is. */
  keywordLabels?: ReadonlyMap<string, string>;
  /** The vocabulary's category keys in order, for the avatar colour. */
  categoryOrder?: readonly string[];
}) {
  const intl = useIntl();
  const team = entry.kind === "team";
  const members = entry.members ?? [];
  const memberOf = entry.memberOf ?? [];
  const original = isOriginal(entry);
  const elsewhere = entry.siteId !== siteId;
  const chips: CardChip[] = [];
  if (!team && memberOf.length === 1 && memberOf[0])
    chips.push({
      key: "team",
      team: true,
      label: intl.formatMessage({ id: "tpl.card.alsoInTeam" }, { name: memberOf[0].name }),
    });
  if (!team && memberOf.length > 1)
    chips.push({
      key: "team",
      team: true,
      label: intl.formatMessage({ id: "tpl.card.inTeams" }, { count: memberOf.length }),
      title: memberOf.map((parent) => parent.name).join(", "),
    });
  const labels = new Set([
    ...(entry.categoryKey ? [categoryLabels.get(entry.categoryKey) ?? entry.categoryKey] : []),
    // The Original marker by the name says it once; the chip row does not repeat it.
    ...[...(entry.tagKeys ?? []), ...(entry.capabilityKeys ?? [])]
      .filter((key) => key !== ORIGINAL_KEYWORD)
      .map((key) => keywordLabels?.get(key) ?? key),
  ]);
  for (const label of labels) chips.push({ key: `label:${label}`, label });
  const shownChips = chips.slice(0, CHIPS_SHOWN);
  const hiddenChips = chips.slice(CHIPS_SHOWN);
  const cardSty: CSSProperties = {
    position: "relative",
    background: card,
    border: `var(--brand-bw-2-5) solid ${ink}`,
    borderRadius: "var(--brand-r-18)",
    boxShadow: "var(--brand-shadow-4)",
    padding: "18px",
    cursor: "pointer",
    overflow: "hidden",
    textAlign: "left",
    minWidth: 0,
  };
  return (
    <div data-library-key={entry.libraryKey} data-site={entry.siteId} style={cardSty}>
      <div style={{ display: "flex", alignItems: "center", gap: "11px" }}>
        <div
          aria-hidden="true"
          style={{
            width: "42px",
            height: "42px",
            flex: "none",
            border: `var(--brand-bw-2-5) solid ${ink}`,
            borderRadius: "var(--brand-r-12)",
            boxShadow: "var(--brand-shadow-2-5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: "var(--brand-font-display)",
            fontWeight: 800,
            fontSize: "19px",
            color: card,
            background: templateColor(entry.categoryKey, categoryOrder),
          }}
        >
          {templateLetter(entry.name)}
        </div>
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: "6px",
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          {elsewhere ? <HomeSiteBadge siteId={entry.siteId} /> : null}
          {team ? <TeamBadge count={members.length} /> : null}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "13px" }}>
        <h2
          className="mfa-clip"
          style={{
            minWidth: 0,
            margin: 0,
            fontFamily: "var(--brand-font-display)",
            fontWeight: 800,
            fontSize: "16.5px",
          }}
        >
          <Link to={to} state={state} className={styles.titleLink}>
            {entry.name}
          </Link>
        </h2>
        {original ? (
          <span
            data-testid="original-marker"
            style={{
              flex: "none",
              padding: "1px 7px",
              border: "var(--brand-bw-2) solid var(--brand-line-2)",
              borderRadius: "var(--brand-r-999)",
              fontFamily: "var(--brand-font-display)",
              fontWeight: 800,
              fontSize: "10px",
              color: "var(--brand-muted-2)",
            }}
          >
            {keywordLabels?.get(ORIGINAL_KEYWORD) ??
              intl.formatMessage({ id: "tpl.card.original" })}
          </span>
        ) : null}
      </div>
      <p
        data-testid="card-description"
        className={styles.description}
        style={{
          margin: "4px 0 0",
          fontWeight: 600,
          fontSize: "13px",
          color: "var(--brand-muted-2)",
          lineHeight: 1.5,
          minHeight: "38px",
        }}
      >
        {entry.description}
      </p>
      {team && members.length ? (
        <div
          data-testid="team-members"
          style={{
            fontWeight: 700,
            fontSize: "12px",
            color: "var(--brand-muted)",
            lineHeight: 1.45,
            marginTop: "8px",
          }}
        >
          <FormattedMessage
            id="tpl.card.members"
            values={{
              names: members
                .slice(0, MEMBER_NAMES_SHOWN)
                .map((member) => member.name)
                .join(", "),
              more: Math.max(0, members.length - MEMBER_NAMES_SHOWN),
            }}
          />
        </div>
      ) : null}
      {chips.length ? (
        <div
          data-testid="card-chips"
          style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "11px" }}
        >
          {shownChips.map((chip) => (
            <div
              key={chip.key}
              {...(chip.team ? { "data-testid": "also-in-team" } : {})}
              style={chip.team ? teamChipSty : chipSty}
              title={chip.title}
            >
              {chip.label}
            </div>
          ))}
          {hiddenChips.length ? (
            <div
              data-testid="more-chips"
              style={chipSty}
              title={hiddenChips.map((chip) => chip.label).join(", ")}
            >
              <FormattedMessage id="tpl.card.moreChips" values={{ count: hiddenChips.length }} />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
